import {
  type Color,
  type Direction,
  EAST,
  forwardFacing,
  NORTH,
  paddedCells,
  parseSquare,
  PIECE_LETTERS,
  type PieceType,
  SOUTH,
  square,
  squareName,
  WEST
} from "src/engine/rules/board";
import type { Command } from "src/engine/rules/commands";
import { KING_FACTORY, parseFen } from "src/engine/rules/position";
import { rollDeposits } from "./deposits";

// Starting positions for single player.
//
// The board opens empty of scrap (material is earned), so a full game takes a
// while to get its economy going. That is the right pace for two players and
// the wrong pace for testing the roguelike loop, which only begins once the
// first opponent is gone — hence a scenario that hands you one enemy and a
// pile to mine, and gets you to the interesting part in a couple of minutes.

/**
 * The factions a board can be set up with.
 *
 * **One table, because a scenario's `kings`, a level's `army` object and the
 * scenario screen's picker are the same vocabulary.** Only the factory army
 * since §31 R2 retired the army styles; the table is the faction hook.
 */
export const ARMIES = [
  {
    id: "factory",
    name: "Factory",
    kind: KING_FACTORY,
    detail: "The ordinary game. The king refines, the rooks mint, the pawns dig."
  }
] as const;

/** What a scenario may name in `kings`, and what the picker may offer. */
export type ArmyId = (typeof ARMIES)[number]["id"];

export interface Scenario {
  id: string;
  name: string;
  detail: string;
  fen: string;
  /** Scrap on the board at the start, as square -> amount. */
  heaps?: Record<string, number>;
  /**
   * Coal lying loose at the start, as square -> amount (§31 M2). **Absent is
   * a match for every heap** — as much coal on each scrap square as scrap — so
   * every rung of that metal can be paid for; give `{}` for none.
   */
  coal?: Record<string, number>;
  /** Belt parts already laid, as square -> compass direction. */
  belts?: Record<string, "n" | "e" | "s" | "w">;
  /** Material already in a piece's hands, as square -> grade -> count. */
  stock?: Record<string, Partial<Record<0 | 1 | 2 | 3, number>>>;
  /**
   * Armies the FEN cannot describe, because a FEN has two colours.
   *
   * Grouped by side rather than listed per piece, so an army reads as an army:
   * where it stands is a table you can see the shape of. A pawn takes its
   * side's own `forwardFacing`, which is the whole reason the four-army board
   * below is laid out on the four *edges* — `FORWARD_FACING` already runs
   * north, south, west, east, so every army marches inward without a single
   * facing being written down.
   *
   * A scenario using this opens from an empty FEN and puts **all** its armies
   * here, White and Black included: two tables describing one board is how the
   * two quietly stop agreeing.
   */
  armies?: readonly {
    side: number;
    /** square -> piece letter, as in a FEN. Case is ignored; the side is here. */
    pieces: Record<string, "k" | "q" | "r" | "b" | "n" | "p">;
  }[];
  /**
   * Which faction each side is, by side.
   *
   * Board setup like `armies`, not a difficulty setting: a faction is a rules
   * change, so the only place it can be declared is where the board is. A
   * profile picks an *evaluation*; this picks the game being played.
   *
   * The scenario screen may override it per side — that is a player deciding
   * what to look at, and it wins (`scenarioOpening`).
   */
  kings?: Record<number, ArmyId>;
  /**
   * How far the ground runs past the classic sixty-four, in squares.
   *
   * A margin rather than a list, because that is what these boards are: the
   * ordinary opening with room behind it. `scenarioOpening` expands it into
   * the `cells` the command carries, so the *board* is still described one
   * square at a time where it lands in the log.
   */
  pad?: number;
  /**
   * May this board's armies be divided into teams?
   *
   * A gate, not a layout: the layouts are `ALLIANCE_LAYOUTS` and the screen's
   * pick is global, so this is each board saying whether that pick means
   * anything on it. **Only a board with more than two armies may say yes** —
   * allying the only two sides there are is a game in which nobody can take
   * anything, which is not a variant, it is a board that cannot end.
   */
  teams?: boolean;
}

/** A named way to divide a board's armies up. See `Scenario.teams`. */
export interface AllianceLayout {
  id: string;
  name: string;
  detail: string;
  /**
   * Alliance id per side, as the `new-game` command takes it. Sides sharing a
   * number are allies. Entries past the board's own armies are ignored, so one
   * table serves any number of them.
   */
  sides: readonly number[];
}

/**
 * How a board's armies may be divided.
 *
 * **Free-for-all first, because it is what a board opens as** and what the
 * team layouts are a departure from. Written for the four-edged board, where
 * the two ways to pair four armies are genuinely different games — across the
 * board, or round it — rather than the same game relabelled.
 */
export const ALLIANCE_LAYOUTS: readonly AllianceLayout[] = [
  {
    id: "free",
    name: "Free-for-all",
    detail: "Every army for itself. The last king standing wins.",
    sides: [0, 1, 2, 3]
  },
  {
    id: "opposite",
    name: "Opposite pairs",
    detail: "Each army allied with the one facing it, so a team holds two opposite edges.",
    sides: [0, 0, 1, 1]
  },
  {
    id: "adjacent",
    name: "Neighbours",
    detail: "Each army allied with a neighbour, so a team holds a corner and the front is a diagonal.",
    sides: [0, 1, 1, 0]
  }
];

/**
 * How many armies a board opens with.
 *
 * Two is the floor because a FEN has two colours — the one-army board is a FEN
 * with an empty side, and `parseFen` gives it two all the same. Past that it is
 * whatever `armies` names, which is the only way to open with a third.
 *
 * Derived rather than declared, so a board cannot say one number and stand up
 * another: the scenario screen sizes its per-army controls off this, and a
 * board with a row it does not have is a pick that reaches nothing.
 */
export function sidesOf(scenario: Scenario): number {
  let sides = 2;
  for (const army of scenario.armies ?? []) sides = Math.max(sides, army.side + 1);
  return sides;
}

/**
 * A team assignment with its teams renumbered in order of first appearance.
 *
 * **An alliance id is a name, so what matters is the partition rather than the
 * numbers.** `[1,1,0,0]` and `[0,0,1,1]` are the same two teams facing each
 * other, and without this the screen would call one of them Opposite pairs and
 * the other Custom — and two players who set up the same game would hash
 * differently, since `alliances` is in the hash whenever anybody is allied.
 */
export function canonicalTeams(
  teams: readonly number[],
  sides: number
): readonly number[] {
  const seen = new Map<number, number>();
  return Array.from({ length: sides }, (_, side) => {
    const team = teams[side] ?? side;
    if (!seen.has(team)) seen.set(team, seen.size);
    return seen.get(team)!;
  });
}

/**
 * Which named layout this assignment *is*, or undefined for one nobody named.
 *
 * Derived, never stored beside the assignment: the screen offers the layouts as
 * quick fills and lets a player edit any army's team afterwards, so "which
 * preset is on" has to be a question about the teams rather than a second piece
 * of state that stops agreeing with them.
 */
export function layoutFor(
  teams: readonly number[],
  sides: number
): AllianceLayout | undefined {
  const want = canonicalTeams(teams, sides).join();
  return ALLIANCE_LAYOUTS.find(
    (layout) => canonicalTeams(layout.sides, sides).join() === want
  );
}

/** Nobody's board: every army in this scenario is placed by `armies`. */
const EMPTY_BOARD = "8/8/8/8/8/8/8/8 w - - 0 1";

/**
 * A block of scrap, as the heap table wants it.
 *
 * Written out rather than listed square by square because these are *fields* —
 * a padded board's deposits run to a couple of dozen squares, and a table that
 * long is one nobody can see the shape of. Outside the classic eight files a
 * square's name is `f<file>r<rank>`, which is exactly what `parseSquare`
 * round-trips, so the record is still the same record.
 */
function scrapField(
  files: readonly number[],
  ranks: readonly number[],
  amount: number
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const rank of ranks) {
    for (const file of files) out[squareName(square(file, rank))] = amount;
  }
  return out;
}

/**
 * A raiding party: a king and four fighting pieces, laid out in two lines.
 *
 * Written once and placed twice, because the whole point of the skirmish is
 * that the two camps are *evenly matched* — two tables of five squares each is
 * two chances for one of them to quietly get the better bishop, and nothing on
 * the board would say so.
 *
 * **The king is not decoration.** A side with no king reads as checkmate the
 * moment it holds the clock (`gameStatus`, gated on somebody else having one),
 * and in a scenario that turns a front over and lands a wave — so a kingless
 * camp would end the encounter on the turn it started.
 */
function raidingParty(file: number, rank: number): Record<string, "k" | "r" | "b" | "n"> {
  return {
    [squareName(square(file, rank + 1))]: "r",
    [squareName(square(file + 1, rank + 1))]: "k",
    [squareName(square(file, rank))]: "n",
    [squareName(square(file + 1, rank))]: "b",
    [squareName(square(file + 2, rank))]: "n"
  };
}

// **The padded board is the rules' now**, because `maps.ts` opens boards of
// this shape and `src/engine/rules/` may not import from here. Re-exported rather
// than re-implemented: a scenario padded two out and the Factory Map have to
// be the same squares, or they are two boards that look like one.
export { paddedCells };

/**
 * One army's worth of chess, laid along a line.
 *
 * The four-way board wants the same sixteen pieces four times over, each on a
 * different edge and each facing in — so the layout is written once, as *back
 * rank* and *pawns*, and the four armies are that table read along a rank or
 * along a file. Written out by hand it is sixty-four entries and four chances
 * to put a king on the wrong square.
 */
const BACK_RANK = ["r", "n", "b", "q", "k", "b", "n", "r"] as const;

function chessArmy(
  /** Which way the army's two lines run: along a rank, or along a file. */
  along: "rank" | "file",
  /** The line the back rank stands on, and the one the pawns stand on. */
  back: number,
  pawns: number
): Record<string, "k" | "q" | "r" | "b" | "n" | "p"> {
  const out: Record<string, "k" | "q" | "r" | "b" | "n" | "p"> = {};
  for (let i = 0; i < 8; i++) {
    const home = along === "rank" ? square(i, back) : square(back, i);
    const ahead = along === "rank" ? square(i, pawns) : square(pawns, i);
    out[squareName(home)] = BACK_RANK[i];
    out[squareName(ahead)] = "p";
  }
  return out;
}

export const SCENARIOS: Scenario[] = [
  {
    id: "full",
    name: "Full board",
    detail:
      "A normal game of chess against the opponent, with the factory on top. " +
      "Everything has to be earned: no scrap exists until something dies.",
    fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
  },
  {
    id: "skirmish",
    name: "Skirmish",
    detail:
      "A small enemy force and scrap already on the ground. Built to reach " +
      "the expansion quickly: take the two pieces, mine what they leave, and " +
      "the board opens up.",
    fen: "4k3/8/8/3n4/8/2P1P3/3PKP2/3R4 w - - 0 1",
    heaps: { d4: 6, e4: 4 }
  },
  {
    id: "wide",
    name: "Wide skirmish",
    detail:
      "The ordinary opening on a board four squares deeper on every side, " +
      "with a seam of scrap behind each army. The economy opens on turn one " +
      "and the ground worth holding is behind you, not between you.",
    fen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    // **Four out on every side**, so each army has a rear area about as deep
    // as the board it is fighting across. Anything less and a deposit sits
    // inside the opening's own pawn structure; anything more and the walk is
    // longer than the game.
    pad: 4,
    // **Behind, not between.** The whole point of the padding is that mining
    // is a thing you do at *home*, so the pull of the economy is backwards
    // and the pull of the war is forwards — which is the tension the empty
    // board only reaches once somebody has died. Deep piles rather than many
    // shallow ones: a heap is worked by one pawn standing on it, so a wide
    // thin seam is a queue.
    heaps: {
      ...scrapField([1, 2, 3, 4, 5, 6], [-2, -3], 12),
      ...scrapField([1, 2, 3, 4, 5, 6], [9, 10], 12)
    }
  },
  {
    id: "four-way",
    name: "Four-way war",
    detail:
      "Four full armies, one to an edge of a board padded two squares out, " +
      "with the scrap in the middle. Who is allied with whom is a setting — " +
      "free-for-all, or two teams of two facing each other.",
    fen: EMPTY_BOARD,
    // **Two out, which is exactly the recess.** Each army stands on the two
    // lines *behind* the classic board's edge, so the sixty-four squares in
    // the middle are the ground nobody starts on — a no man's land the size
    // of a chessboard, with four armies looking across it.
    pad: 2,
    // The contested middle, and the only material on the board. Four armies
    // reaching for one field is the whole point: the economy and the fight are
    // the same argument, which on a two-player board they only become once
    // somebody has died. Sixteen squares of it, because there are sixty-four
    // pieces here — four heaps between four full armies is a queue, not a war.
    heaps: scrapField([2, 3, 4, 5], [2, 3, 4, 5], 8),
    // **One army per edge, and the facings are free.** `FORWARD_FACING` runs
    // north, south, west, east, so side 0 comes up the board, side 1 comes
    // down it, and sides 2 and 3 come in from the sides — every pawn already
    // points at the middle without a facing being written here.
    //
    // A full sixteen each, laid out by `chessArmy` rather than by hand: the
    // corners of the padding stay empty, so four eight-wide armies fit on a
    // twelve-square board without a single overlap. Nobody has a home rank
    // (`pawnStartRank` is the classic one), so there are no double steps —
    // right for an army that is dug in rather than starting a game.
    armies: [
      { side: 0, pieces: chessArmy("rank", -2, -1) },
      { side: 1, pieces: chessArmy("rank", 9, 8) },
      { side: 2, pieces: chessArmy("file", 9, 8) },
      { side: 3, pieces: chessArmy("file", -2, -1) }
    ],
    // The one board with enough armies for the question to mean anything.
    teams: true
  },
  {
    id: "encounter",
    name: "The skirmish",
    detail:
      "Two raiding parties land in the north with no love for each other or " +
      "for you, evenly matched and close enough to start. Wade in and fight " +
      "both, or build while they thin each other out and take the field — " +
      "and whatever they leave on it.",
    fen: EMPTY_BOARD,
    // Four out, so there is real distance between your ground and theirs.
    // Engaging has to be a decision rather than a thing that happens to you on
    // turn three, and that is a question about how far away they are.
    pad: 4,
    // **Nobody is allied, and that is the default rather than a setting.**
    // Every side starts in an alliance of its own (`Position.alliances`), so a
    // free-for-all is what you get by not saying anything — and it is what
    // makes the two camps fight: `nearestEnemyPiece` picks whoever is closest,
    // which for either of them is the other one.
    armies: [
      { side: 0, pieces: chessArmy("rank", 0, 1) },
      { side: 1, pieces: raidingParty(0, 9) },
      { side: 2, pieces: raidingParty(5, 9) }
    ],
    // Enough to open the economy behind your own line, and no more. The real
    // prize is the battlefield: two armies killing each other leave the scrap
    // where they fell, and it is nearer to whoever survived the argument.
    heaps: scrapField([2, 3, 4, 5], [-2, -3], 9)
  },
  {
    id: "sandbox",
    name: "Quiet works",
    detail:
      "One factory, a field of scrap, and nobody to fight. Nothing can " +
      "threaten it and nothing arrives, so what you are watching is the " +
      "industry on its own: dig, cook, haul, mint. The measuring rig.",
    // **One army, and the second side is simply not there.** `nextSide` skips
    // a side with no pieces, so the clock never leaves White and the empty
    // seat is never asked whether it has been mated — which is what keeps
    // this a board rather than a game that ends on move one.
    fen: "8/8/8/8/8/8/PPPPPPPP/RNBQKBNR w - - 0 1",
    pad: 2,
    // Scrap on every side of it, at every size, so the choice of what to work
    // is a real one rather than a walk to the only pile.
    heaps: {
      ...scrapField([0, 1, 2, 3, 4, 5, 6, 7], [-2], 16),
      ...scrapField([-2, -1], [0, 1, 2, 3], 10),
      ...scrapField([8, 9], [0, 1, 2, 3], 10),
      ...scrapField([1, 3, 5], [3, 5], 8),
      ...scrapField([2, 4, 6], [4, 6], 6)
    }
  }
];

/**
 * Everything the scenario picker offers.
 *
 * The same list as `SCENARIOS`, and it is kept as a name because the picker
 * and the tools ask a different question of it than the table does. It used to
 * be `SCENARIOS` plus a second table of **set pieces** — positions written to
 * answer one question about the factory and then left in the menu — and every
 * one of those was a debugging fixture wearing a board's clothes. A menu that
 * offers a lane pointing the wrong way beside a four-army war is not a menu of
 * things to play. A test that wants such a board now builds it, which is where
 * a fixture belongs.
 */
export const ALL_SCENARIOS: Scenario[] = SCENARIOS;

/**
 * The most armies any board has, which is what the screen's state is sized to.
 *
 * **Below `SCENARIOS`, and that is not tidiness.** It reads the table, so
 * declared above it this is a temporal dead zone — the module throws on import
 * and the page is blank, which is the trap `layout.ts` has its own paragraph
 * about.
 */
export const MOST_SIDES = Math.max(...SCENARIOS.map(sidesOf));

/**
 * Is this actually one of ours?
 *
 * `scenarioById` falls back to the first scenario for anything it does not
 * know, which is right for a bad URL and wrong for a caller asking "should I
 * set a scenario up at all". `GameShell` is handed `"run"` as a marker meaning
 * "single player, drive the opponent" — and treated it as an id, so a run laid
 * the ordinary chess opening over its level. The level dispatch usually landed
 * afterwards and hid it; on a *loaded* run there is no level dispatch, and the
 * save was overwritten by a fresh game the moment it opened.
 */
export const hasScenario = (id: string): boolean =>
  ALL_SCENARIOS.some((entry) => entry.id === id);

export const scenarioById = (id: string): Scenario =>
  ALL_SCENARIOS.find((entry) => entry.id === id) ?? SCENARIOS[0];

/** A scenario's coal as the command carries it — see `Scenario.coal`. */
function coalOf(scenario: Scenario): { coal?: [number, number][] } {
  const coal = Object.entries(scenario.coal ?? scenario.heaps ?? {}).map(
    ([name, amount]) => [parseSquare(name), amount] as [number, number]
  );
  return coal.length > 0 ? { coal } : {};
}

/** Compass letters as a scenario writes them, to the engine's directions. */
const COMPASS: Record<string, Direction> = { n: NORTH, e: EAST, s: SOUTH, w: WEST };

/** A scenario's army names, to the king kinds the rules hold. */
const KING_KINDS = Object.fromEntries(
  ARMIES.map((army) => [army.id, army.kind])
) as Record<ArmyId, number>;

/**
 * A dense array of king kinds, indexed by side.
 *
 * Dense because the command's field is positional: a sparse table would leave
 * holes that read as side 0, which is exactly the side a scenario is least
 * likely to have meant.
 *
 * **A pick beats the declaration**, the same way `new-game` treats this whole
 * field as the one a player made on purpose: a scenario's `kings` is how the
 * board was authored, and the screen's picker is somebody asking to see
 * something else on it. An unpicked side keeps what the scenario said, so
 * opening a board and changing nothing gives you the board's own factions.
 */
function kingKindsOf(scenario: Scenario, picked: readonly (number | undefined)[]): number[] {
  const sides = Math.max(
    0,
    picked.length,
    ...Object.keys(scenario.kings ?? {}).map((side) => Number(side) + 1)
  );
  return Array.from(
    { length: sides },
    (_, side) => picked[side] ?? KING_KINDS[scenario.kings?.[side] ?? "factory"]
  );
}

/**
 * Every square a scenario stands a piece on — the FEN's armies and whatever
 * `armies` adds past the two a FEN can name.
 *
 * Deposits are generated away from **all** of them: a seam under somebody's
 * opening rank is not a seam anybody has to reach for, and on a padded board
 * the margin is exactly the ground that is nobody's yet.
 */
function scenarioPieces(scenario: Scenario): number[] {
  const out = [...parseFen(scenario.fen).board.keys()];
  for (const army of scenario.armies ?? []) {
    for (const name of Object.keys(army.pieces)) out.push(parseSquare(name));
  }
  return out;
}

/**
 * The opening command a scenario is.
 *
 * **One definition, because there were two and they disagreed.** A scenario is
 * set up in two places — `GameShell` opens one, `App`'s Restart re-rolls it —
 * and each carried its own flattening of the same table. The opening's copy
 * knew about `fen` and `heaps` and nothing else, so every set piece with a
 * lane or a stocked hopper opened *without* them and only came right if you
 * pressed Restart. A four-army board is what made it obvious, because an army
 * that goes missing is louder than a belt that does.
 *
 * Flattened here rather than in either caller: the scenario groups by army and
 * by square because that is how a board reads, and the command takes flat
 * lists because that is what a log carries.
 *
 * Both callers pass `kings` for the same reason: a Restart that dropped the
 * player's armies would be that same disagreement arriving a second time.
 */
export function scenarioOpening(
  scenario: Scenario,
  seed: number,
  /**
   * A king kind per side from the scenario screen, `undefined` where the
   * player left the board's own answer alone.
   */
  kings: readonly (number | undefined)[] = [],
  /**
   * Which team each army is on, as `new-game.alliances` takes it — sides
   * sharing a number are allies.
   *
   * **The assignment itself, not the name of a layout.** The screen offers the
   * named layouts as quick fills and then lets a player move any single army,
   * so most of what it can express has no name; carrying an id would mean the
   * only teams that could reach the board are the three somebody wrote down.
   *
   * Ignored by a board that offers none, which is every two-army board.
   */
  teams?: readonly number[],
  /**
   * Which game to play (`rules/ruleset.ts`, §26 F1), `undefined` for the
   * default. A value, never a flag read — `src/engine/ai/` is barred from the flags
   * module, so the shell resolves it and hands it down, `wave-delay`'s shape.
   */
  ruleset?: Extract<Command, { type: "new-game" }>["ruleset"]
): Extract<Command, { type: "new-game" }> {
  // **Gated on the board, never on the pick.** Every layout but free-for-all
  // would ally the only two sides a two-army board has — a game nobody can win,
  // arrived at by touching a control about a different board.
  //
  // Canonicalised, so two players who set the same teams up under different
  // numbers open the same game and hash the same.
  const alliances =
    scenario.teams && teams ? canonicalTeams(teams, sidesOf(scenario)) : undefined;
  const cells = scenario.pad ? paddedCells(scenario.pad) : undefined;
  return {
    type: "new-game",
    seed,
    fen: scenario.fen,
    cells,
    // Fields on the ground the padding added, away from every army (§26 F3).
    //
    // **No lobe here**: a scenario board is a *shape somebody chose*, so a
    // field that grew ground to fit itself would be editing the board the
    // scenario is. A run's openings are the other way round — see
    // `rollDeposits`' `board` option.
    //
    // The clearance is what keeps them out of the contested middle rather than
    // a rule about which squares are the classic sixty-four: on the wide board
    // every army is on its home rank, so ground far enough from both of them is
    // the margin behind them — the pull of the economy backwards and the pull
    // of the war forwards, which is what that board is for. On the four-way
    // board the armies ring the outside and the same test leaves the *centre*,
    // which is that board's own answer to the same question.
    //
    // **Gated on the board having room**, not merely on the count coming out at
    // nought: the classic sixty-four is the board two players open on, and an
    // opening command carrying an empty list is no longer the one every existing
    // log holds. Authored levels are untouched for the other half of that rule —
    // a level authors its own economy.
    deposits: cells
      ? rollDeposits(seed, 0, cells, scenarioPieces(scenario)).rows
      : undefined,
    alliances,
    ruleset,
    heaps: Object.entries(scenario.heaps ?? {}).map(
      ([name, amount]) => [parseSquare(name), amount] as [number, number]
    ),
    // Gated like `deposits`: an opening with no coal carries no field, so a
    // board with no scrap opens on the command it always did.
    ...coalOf(scenario),
    belts: Object.entries(scenario.belts ?? {}).map(
      ([name, dir]) => [parseSquare(name), COMPASS[dir], 0] as [number, Direction, Color]
    ),
    kingKinds: kingKindsOf(scenario, kings),
    stock: Object.entries(scenario.stock ?? {}).flatMap(([name, byGrade]) =>
      Object.entries(byGrade).map(
        ([grade, count]) =>
          [parseSquare(name), Number(grade), count] as [number, number, number]
      )
    ),
    // Facing is the side's own forward, which is why the four-way board is laid
    // out on the four *edges*: `FORWARD_FACING` already runs north, south,
    // west, east, so every army marches inward with nothing written down.
    armies: (scenario.armies ?? []).flatMap((army) =>
      Object.entries(army.pieces).map(
        ([name, letter]) =>
          [
            parseSquare(name),
            army.side as Color,
            PIECE_LETTERS.indexOf(letter) as PieceType,
            forwardFacing(army.side as Color)
          ] as [number, Color, PieceType, Direction]
      )
    )
  };
}
